import {InjectionToken} from '@angular/core';
import {CommonsConfig} from './commons/commons.config';
import {ApiConfig} from './api/api-config';
import {AppColors, SharedConfig} from './shared/shared.config';
import {QuantityType} from 'smag-commons/dto/msg';
import {EntityMeasureAggregationType, Scope} from 'smag-commons/dto/api';
import {AuthConfig} from 'auth-commons';
import {environment} from 'src/environments/environment';
import {I18NConfig} from './i18n/i18n.service';
import {EMAIL_PATTERN} from './shared/patterns';

export class AppConfig {
    commons: CommonsConfig;
    shared: SharedConfig;
    api: ApiConfig;
    i18n: I18NConfig;
    auth: AuthConfig;
    toasterTimeout: number;
    exprEditor: ExpressionEditorConfig;
    resources: ResourcesConfig;
    measures: MeasuresConfig;
    totpTTL?: number;
}

export const APP_CONFIG_TOKEN = new InjectionToken<AppConfig>('app.config');

const DEFAULT_COLOR = environment.defaultColor;

const PREFIX_PROXY_AUTH_COMMONS = environment.proxyAuthCommons?.length ? environment.proxyAuthCommons : '';

export const APP_COLORS: AppColors = environment.appColors as AppColors;

export const APP_CONFIG: AppConfig = {
    commons: {
        name: 'ROSE EMS',
        version: '0.0.0' // NOTE: retrieved from backend, do not use
    },
    shared: {
        // empty maps are overridden explicitly in app.module.ts in order to avoid "Expression form not supported" error while compiling (bracket notation not supported in Angular metadata)
        colors: APP_COLORS,
        quantityColors: {},
        aggregationColors: {},
        aggregationColorsConsolidation: {},
        defaultColor: DEFAULT_COLOR,
        gridColor: '#E0E0E0',
        tooltipBorderColor: '#EEEEEE',
        tooltipBackgroundColor: 'white',
        selectionLineColor: '#BDBDBD'
    },
    api: {},
    i18n: {},
    auth: {
        scope: Scope.EMS,
        proxiedAuthServer: true, // use api-server for everything
        tokenKey: 'app-token',
        loginEndpoint: '/login',
        renewTokenEndpoint: '/renew',
        logoutEndpoint: '/logout',
        loginRoute: '/c/login',
        forbiddenRoute: '/f/forbidden',
        changePasswordRoute: '/c/change-password',
        recoverPasswordRoute: PREFIX_PROXY_AUTH_COMMONS + '/c/recover-password',
        acceptClausesRoute: '/c/accept-clauses',
        activateTotpRoute: '/c/activate-totp',
        homeRoute: '/c/dev-dashboard',
        defaultTimeZone: 'Europe/Rome',
        rootPath: environment.rootPath,
        domainPrefix: environment.domainPrefix,
        usernamePattern: /^[a-zA-Z0-9]([_.-]?[a-zA-Z0-9]+)*$/, // initial underscore not allowed in frontend (reserved for machine users)
        emailPattern: EMAIL_PATTERN,
        firstnamePattern: /^[a-zA-ZÀÈÌÒÙàèìòù'][a-zA-ZÀÈÌÒÙàèìòù'\-]*([ a-zA-ZÀÈÌÒÙàèìòù'\-]+)*$/,
        lastnamePattern: /^[a-zA-ZÀÈÌÒÙàèìòù'][a-zA-ZÀÈÌÒÙàèìòù'\-]*([ a-zA-ZÀÈÌÒÙàèìòù'\-]+)*$/,
        emailAsUsername: true,
        emailHintAtLogin: true,
        totpTTL: environment.totpTTL
    },
    toasterTimeout: 10000,
    exprEditor: {
        builtinTypesColor: '#63d3e8',
        builtinFunctionsColor: '#a4dd2e',
        builtinsBold: true,
        variableAccessorsColor: '#904748',
        variableAccessorBold: true,
        variablesColor: '#eb6d21',
        variablesBold: true
    },
    resources: {
        aggregationCurrentValueRefreshInterval: 60
    },
    measures: {
        showCurrentValue: environment.measuresShowCurrentValue
    },
    totpTTL: environment.totpTTL
};

export const APP_CONFIG_QUANTITY_COLORS: { [qt in QuantityType]?: string } = {
    [QuantityType.ACTIVE_POWER]: APP_COLORS.activePower,
    [QuantityType.REACTIVE_POWER]: APP_COLORS.reactivePower,
    [QuantityType.THERMAL_POWER]: APP_COLORS.thermalPower,
    [QuantityType.INPUT_THERMAL_POWER]: APP_COLORS.thermalPower,
    [QuantityType.STATE_OF_CHARGE]: APP_COLORS.stateOfCharge,
    [QuantityType.VOLTAGE]: APP_COLORS.voltage,
    [QuantityType.PHASE_ANGLE]: APP_COLORS.phaseAngle,
    [QuantityType.INVERSE_ACTIVE_POWER]: APP_COLORS.activePower,
    [QuantityType.INVERSE_REACTIVE_POWER]: APP_COLORS.reactivePower,
    [QuantityType.CURTAILED_POWER]: APP_COLORS.curtailedPower,
    [QuantityType.SHARED_POWER]: APP_COLORS.sharedPower,
    [QuantityType.GENERATED_POWER]: APP_COLORS.generatedPower,
    [QuantityType.CONSUMED_POWER]: APP_COLORS.consumedPower,
    [QuantityType.INJECTED_POWER]: APP_COLORS.injectedPower,
    [QuantityType.ABSORBED_POWER]: APP_COLORS.absorbedPower,
    [QuantityType.STORAGE_POWER]: APP_COLORS.storagePower
};

export const APP_CONFIG_AGGREGATION_COLORS: { [at in EntityMeasureAggregationType]?: string } = {
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION]: APP_COLORS.generatedPower,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_CONSUMPTION]: APP_COLORS.consumedPower,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION]: APP_COLORS.selfConsumedEnergy,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION]: APP_COLORS.injectedPower,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION]: APP_COLORS.absorbedPower,
    [EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY]: APP_COLORS.sharedPower,
    [EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY_RATIO]: APP_COLORS.sharedEnergyRatio
};

export const APP_CONFIG_AGGREGATION_COLORS_CONSOLIDATED: { [at in EntityMeasureAggregationType]?: string } = {
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_GENERATION]: APP_COLORS.consolidatedGenerated,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_CONSUMPTION]: APP_COLORS.consolidatedConsumed,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_SELF_CONSUMPTION]: APP_COLORS.consolidatedSelfConsumed,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_INJECTION]: APP_COLORS.consolidatedInjected,
    [EntityMeasureAggregationType.COMMUNITY_ENERGY_ABSORPTION]: APP_COLORS.consolidatedAbsorbed,
    [EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY]: APP_COLORS.consolidatedShared,
    [EntityMeasureAggregationType.COMMUNITY_SHARED_ENERGY_RATIO]: APP_COLORS.consolidatedSharedRatio
};

export interface ExpressionEditorConfig {
    builtinTypesColor: string;
    builtinFunctionsColor: string;
    builtinsBold: boolean;
    variableAccessorsColor: string;
    variableAccessorBold: boolean;
    variablesColor: string;
    variablesBold: boolean;
}

export interface ResourcesConfig {
    aggregationCurrentValueRefreshInterval: number; // in seconds
}

export interface MeasuresConfig {
    showCurrentValue: boolean;
}
